<script setup lang="ts">
import type { __SellerContactsFragment, __SocialLinkType } from '../generated/graphql'

const t2 = useT2Base({
  Email: 'El. paštas',
  Tel: 'Tel',
  Behind: 'Atgal',
  Ahead: 'Pirmyn',
  'Local time': 'Vietos laikas',
  'Open 24 hours': 'Dirbame visą parą',
  Offline: 'Uždaryta',
  Closed: 'Uždaryta',
  Monday: 'Pirmadienis',
  Tuesday: 'Antradienis',
  Wednesday: 'Trečiadienis',
  Thursday: 'Ketvirtadienis',
  Friday: 'Penktadienis',
  Saturday: 'Šeštadienis',
  Sunday: 'Sekmadienis',
  Online: 'Dirbame',
  'Customer support': 'Klientų aptarnavimas',
  'Opening hours': 'Darbo valandos',
})
const isMobile = useMobile()

const props = defineProps<{
  onlysocial?: boolean
  reverseColor?: boolean
  contacts: None | __SellerContactsFragment
}>()

const socialValues: Record<__SocialLinkType, { icon: Parameters<typeof defineAsyncComponent>[0]; class?: string }> = {
  Website: { icon: () => import('./IconGlobe.vue'), class: 'globe' },
  Instagram: { icon: () => import('./IconInstagram.vue') },
  Facebook: { icon: () => import('./IconFacebook.vue') },
  Youtube: { icon: () => import('./IconYoutube.vue') },
  Twitter: { icon: () => import('./IconTwitter.vue') },
  Spotify: { icon: () => import('./IconSpotify.vue') },
  Pinterest: { icon: () => import('./IconPinterest.vue') },
  Tiktok: { icon: () => import('./IconTiktok.vue') },
  FbMessenger: { icon: () => import('./IconMessenger.vue') },
}

const data = shallowReactive({
  hours: false,
  inited: false,
})

const hascontent = computed((): boolean => {
  const doc = props.contacts
  return !!(doc?.tels?.length || doc?.emails?.length || doc?.links?.length || doc?.openingHours || doc?.openingHours?.timezone)
})

const uiSocial = computed(() => {
  const links = props.contacts?.links
  return helpers.arr(Object.entries(socialValues), ([type, values]) => {
    const item = links?.find((i) => i.type === type)
    const link = item ? useSocialUrl(item.type, item.handle) : undefined
    if (!link || !item?.handle) return
    return { link, handle: item.handle, type, icon: defineAsyncComponent(values.icon), class: values.class }
  })
})

type UiLink = {
  items: { key: string; title: string; href: string }[]
  name: string
  key: string
  icon: ReturnType<typeof defineAsyncComponent>
}

const ui = computed((): UiLink[] | undefined => {
  const contacts = props?.contacts
  const arr: UiLink[] = []

  const emails = helpers.arr(contacts?.emails, (email) => {
    if (!email?.length) return
    return { key: email, title: email, href: `mailto:${email}` }
  })

  const tels = helpers.arr(contacts?.tels, (tel) => {
    const title = formatTelNrSimple(tel)
    if (!title?.length) return
    return { key: tel, title, href: `tel:${tel}` }
  })

  if (emails?.length) arr.push({ name: t2('Email'), key: 'email', icon: defineAsyncComponent(() => import('./IconEmail.vue')), items: emails })
  if (tels?.length) arr.push({ name: t2('Tel'), key: 'tel', icon: defineAsyncComponent(() => import('./IconTel.vue')), items: tels })

  return arr.length ? arr : undefined
})

type Hours = __SellerContactsFragment['openingHours']
type Weekday = NN<Hours>['mon']

function openActive(item: Weekday | None, h: number, min: number): 'on' | 'off' | 'none' {
  if (!item) return 'none'
  if (item.open24) return 'on'
  if (item.close) return 'off'
  if (!item.hours?.length) return 'off'

  // Convert current time to a single integer: minutes since midnight
  const currentMinutes = h * 60 + min

  // Loop through all time blocks (this handles breaks perfectly)
  for (const block of item.hours) {
    const open = block.open
    const close = block.close

    if (!open || !close) continue // Skip malformed blocks

    const openMinutes = open.hour * 60 + open.min
    const closeMinutes = close.hour * 60 + close.min

    // Check if current time is within this specific block
    // (inclusive of open time, exclusive of close time)
    if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
      return 'on'
    }
  }

  // If the loop finishes and didn't return 'on',
  // the current time is either during a break, before opening, or after closing.
  return 'off'
}

function uiTime(i: NN<NN<Weekday>['hours']>[0]['open']) {
  return `${helpers.zeroPad(i.hour)}:${helpers.zeroPad(i.min)}`
}
function uiWeekday(label: string, value: Weekday | None) {
  const str = () => {
    if (value?.open24) return t2('Open 24 hours')
    if (value?.close) return t2('Closed')
    return helpers.joinStringCb(', ', value?.hours, (i) => `${uiTime(i.open)}-${uiTime(i.close)}`) ?? ''
  }
  return { label, ui: str(), value }
}

const getDay = new Date().getDay()
const weekday = getDay === 0 ? 6 : getDay - 1

const hours = computed(() => {
  const h = props.contacts?.openingHours
  if (!(h?.fri || h?.mon || h?.sat || h?.sun || h?.thu || h?.timezone || h?.tue || h?.wed)) return

  const days = [
    uiWeekday(t2('Monday'), h?.mon),
    uiWeekday(t2('Tuesday'), h?.tue),
    uiWeekday(t2('Wednesday'), h?.wed),
    uiWeekday(t2('Thursday'), h?.thu),
    uiWeekday(t2('Friday'), h?.fri),
    uiWeekday(t2('Saturday'), h?.sat),
    uiWeekday(t2('Sunday'), h?.sun),
  ]

  return { days, today: data.inited ? days[weekday] : undefined }
})

function changeTimezone(date: Date, timeZone: string) {
  // suppose the date is 12:00 UTC
  // then invdate will be 07:00 in Toronto
  // and the diff is 5 hours
  // so 12:00 in Toronto is 17:00 UTC
  return new Date(date.getTime() * 2 - new Date(date.toLocaleString('en-US', { timeZone })).getTime()) // needs to substract
}

function toTime(d: Date) {
  const timezone = props.contacts?.openingHours?.timezone
  const t1 = timezone?.length ? helpers.tryCatch(() => changeTimezone(d, timezone)) : undefined
  const h = d.getHours()

  const currentTimeDiff = () => {
    if (!t1) return
    const diff = t1.getHours() - h
    return diff < 0 ? `${Math.abs(diff)}h ${t2('Behind')}` : diff > 0 ? `${diff}h ${t2('Ahead')}` : t2('Local time')
  }

  return {
    now: t1 ? `${helpers.zeroPad(t1.getHours())}:${helpers.zeroPad(t1.getMinutes())}:${helpers.zeroPad(t1.getSeconds())}` : undefined,
    diff: currentTimeDiff(),
    online2: openActive(hours.value?.today?.value, h, d.getMinutes()),
  }
}
const time = shallowRef<ReturnType<typeof toTime>>({ diff: undefined, now: '...', online2: 'off' })
const interval = useInterval('now', true)
onMounted(() => {
  time.value = toTime(new Date())
  interval.set(() => (time.value = toTime(new Date())), 1000)
  data.inited = true
})

const online2 = computed(() => time.value.online2)
const online = computed(() => {
  const on = online2.value
  return on === 'on' ? { color: 'green' as const, txt: t2('Online') } : on === 'off' ? { color: 'grey' as const, txt: t2('Offline') } : undefined
})

// https://www.etsy.com/shop/BetaMenswear?ref=profile_header
// https://substack.com/profile/27629216-bruce-feiler?utm_source=about-page
</script>

<template>
  <div v-if="hascontent" class="_col _fs09" :class="[reverseColor ? $style.white : $style.black]">
    <template v-if="!onlysocial">
      <template v-if="hours">
        <div v-if="hours.today || time.now || hours.days?.length" class="_row _ctrY _hm28">
          <span class="_h4 _fs09 _pb1 _colorCur">{{ t2('Customer support') }}</span>
        </div>

        <div class="_hm28 _row _ctrY _gap1 _ls-xs _fw-l">
          <span class="_svg16">
            <LazyIconTime v-if="data.inited" class="_svg16" />
          </span>

          <UiLine :label="ts(hours.today?.label || '...')" :value="hours.today?.ui || '...'" current-color>
            <template #after>
              <LazyButtonMini v-if="data.inited" :aria-label="data.hours ? 'Hide hours' : 'Show hours'" @click="data.hours = !data.hours">
                <LazyIconChevron :open="data.hours" size="_svg12" />
              </LazyButtonMini>
            </template>
          </UiLine>
        </div>

        <div v-if="time.now" class="_hm28 _row _ctrY _gap1 _ls-xs _fw-l">
          <span class="_svg16" />
          <span data-allow-mismatch
            >{{ time.now }} <span v-if="time.diff" class="_c4" data-allow-mismatch> - {{ time.diff }}</span></span
          >
          <span v-if="online" data-allow-mismatch class="_row _gap04 _ctrY _fw-xl _ls-xs _ml04"><UiColorBubble :color="online.color" />{{ online.txt }}</span>
        </div>

        <UiGrow v-if="hours.days?.length" :open="data.hours">
          <div class="_row _ctrY _hm28">
            <h4 class="_pb08 _pt1 _colorCur">{{ t2('Opening hours') }}</h4>
          </div>
          <div class="_pb1 _pl3 _hm28 _w100p">
            <UiLine v-for="item2 in hours.days" :key="item2.label" :label="ts(item2.label)" :value="item2.ui" current-color :color="item2?.label === hours?.today?.label ? online?.color : undefined" />
          </div>
        </UiGrow>
      </template>

      <div v-for="item in ui" :key="item.key" class="_col">
        <div v-for="(item2, index) in item.items" :key="item2.key" class="_row _gap12 _ctrY">
          <a :href="item2.href" target="_blank" rel="noreferrer noopener" class="_hm28 _flexblock _ctrY _gap1 _ls-xs _fw-l" :class="$style.box">
            <span v-if="index === 0 && item.icon" class="_svg16">
              <Component v-if="data.inited" :is="item.icon" class="_svg16" />
            </span>
            <span v-else class="_svg16" />
            <span>{{ item2.title }}</span>
          </a>
          <LazyButtonCopy v-if="data.inited && !isMobile" :copy="() => item2.key" />
        </div>
      </div>
    </template>

    <div :class="onlysocial && '_w100p'" class="_row _wrap _pt1 _gap_col_4 cls-social">
      <a v-for="item in uiSocial" :key="item.type" :class="$style.box" :href="item.link" target="_blank" rel="noreferrer noopener" :aria-label="`${item.handle} ${item.type}`">
        <span class="_mr06 _svg16">
          <Component v-if="data.inited" :is="item.icon" class="_svg16" :class="[item.class && $style[item.class]]" />
        </span>
        <span class="_fw-l">{{ item.handle }}</span>
      </a>
    </div>
  </div>
</template>

<style module>
.white {
  --col: var(--bg);
}
.black {
  --col: var(--black);
}

.box {
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_pl04);
  composes: global(_pr14);
  composes: global(m_pt1);
  composes: global(m_pb1);
  composes: global(d_pt04);
  composes: global(d_pb04);
  composes: global(_relative);
  composes: global(_br99);

  cursor: pointer;
}
.box::after {
  content: '\002197';
  display: block;
  font-size: 8px;
  font-family:
    'Helvetica Neue',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Arial',
    sans-serif;
  transform: translateY(6px) scale(0.2);
  transition:
    opacity 120ms ease-in,
    transform 120ms ease-in;

  opacity: 0;

  top: 3px;
  right: 4px;
  position: absolute;
}
.box:hover::after {
  opacity: 1;
  transform: translateY(0);
}
.box:hover {
  color: var(--blue2);
  background: var(--black5, hsla(240, 7.7%, 94.9%, 0.9));
}
</style>
